'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '../contexts/StaticAuthContext';
import { usePersistedSidebarState } from '../hooks/usePersistedSidebarState';

// ⛔ Chi vede quale voce NON si decide qui: il backend scrive le chiavi
// abilitate in `/auth/session` → `navigation.menu_items`, dai ruoli sulle
// organizzazioni (`backend/app/services/navigation_service.py`). Prima il
// filtro era sul privilegio globale, e il responsabile di un'ASD con privilegio
// `player` non vedeva Organizzazioni, Tesseramenti e Conti.
interface NavItem {
  /** Chiave della voce in `navigation.menu_items`. */
  key: string;
  name: string;
  href: string;
  icon: string;
  description: string;
}

interface NavGroup {
  name: string;
  icon: string;
  items: NavItem[];
}

// Single navigation items
const singleNavigation: NavItem[] = [
  {
    key: 'dashboard',
    name: 'Dashboard',
    href: '/',
    icon: '🏠',
    description: 'Panoramica generale'
  },
  {
    key: 'users',
    name: 'Gestione Utenti',
    href: '/users',
    icon: '👥',
    description: 'Amministra utenti'
  }
];

// Grouped navigation items
const groupedNavigation: NavGroup[] = [
  {
    name: 'Gestione Competizioni',
    icon: '🏆',
    items: [
      {
        key: 'competitions',
        name: 'Competizioni',
        href: '/competitions',
        icon: '🏆',
        description: 'Visualizza competizioni'
      },
      {
        key: 'competition_templates',
        name: 'Modelli Competizioni',
        href: '/competition-templates',
        icon: '📋',
        description: 'Modelli riutilizzabili'
      },
      {
        key: 'discipline_config',
        name: 'Configurazione Discipline',
        href: '/discipline-config',
        icon: '💼',
        description: 'Quote, costi operativi e premi per disciplina'
      },
      {
        key: 'units',
        name: 'Unità / Formazioni',
        href: '/units',
        icon: '🤝',
        description: 'Gestione coppie, squadre e formazioni (plugin-driven)'
      }
    ]
  },
  {
    name: 'Gestione Organizzazioni',
    icon: '🏛️',
    items: [
      {
        key: 'memberships',
        name: 'Tesseramenti',
        href: '/memberships',
        icon: '🎫',
        description: 'Gestione tesseramenti per tutte le tipologie di organizzazione'
      },
      {
        key: 'organizations',
        name: 'Organizzazioni',
        href: '/organizations',
        icon: '🏛️',
        description: 'Gestione di tutte le organizzazioni sportive e associative'
      },
      {
        key: 'venues',
        name: 'Sedi',
        href: '/venues',
        icon: '🏢',
        description: 'Gestione sedi'
      },
      {
        key: 'organization_roles',
        name: 'Ruoli sulle Organizzazioni',
        href: '/settings/roles',
        icon: '🔑',
        description: 'Chi ha quali poteri, e dove. Assegnazione e revoca dei ruoli.'
      }
    ]
  },
  {
    name: 'Gestione Finanziaria',
    icon: '💰',
    items: [
      {
        key: 'credits',
        name: 'Crediti da Incassare',
        href: '/credits',
        icon: '💰',
        description: 'Gestione crediti e scadenze'
      },
      {
        key: 'financial',
        name: 'Conti e rendiconto',
        href: '/financial',
        icon: '💳',
        description: 'Rendiconto annuale, movimenti e pagamenti'
      },
      {
        key: 'notifications',
        name: 'Notifiche da gestire',
        href: '/notifications',
        icon: '🔔',
        description: 'Chi va avvisato (quote, gare, iscrizioni) e chi l\'ha fatto a mano'
      },
      {
        key: 'debits',
        name: 'Debiti da Pagare',
        href: '/debits',
        icon: '📤',
        description: 'Gestione debiti dell\'organizzazione'
      }
    ]
  },
  {
    name: 'Amministrazione Sistema',
    icon: '⚙️',
    items: [
      {
        key: 'sport_disciplines',
        name: 'Discipline Sportive',
        href: '/admin/sport-disciplines',
        icon: '🏃',
        description: 'Gestione discipline sportive del sistema'
      },
      {
        key: 'plugins',
        name: 'Diagnostica Plugin',
        href: '/admin/plugins',
        icon: '🔌',
        description: 'Stato sincronizzazione plugin BE/FE'
      },
      {
        key: 'settings',
        name: 'Configurazioni',
        href: '/admin/settings',
        icon: '🔧',
        description: 'Configurazioni globali del sistema'
      },
      {
        key: 'logs',
        name: 'Logs Sistema',
        href: '/admin/logs',
        icon: '📋',
        description: 'Visualizza logs e audit trail'
      },
      {
        key: 'service_fees',
        name: 'Tariffe della piattaforma',
        href: '/admin/service-fees',
        icon: '🧾',
        description: 'Quanto pagano le organizzazioni per il servizio e per PB Play'
      },
      {
        key: 'cross_organization_units',
        name: 'Unità Cross-Org',
        href: '/admin/units',
        icon: '🤝',
        description: 'Gestione formazioni su tutte le organizzazioni'
      }
    ]
  }
];

export default function Navigation() {
  const pathname = usePathname();
  const router = useRouter();
  const { authState, logout } = useAuth();

  // Use persisted sidebar state hook for localStorage persistence
  const {
    isCollapsed,
    toggleCollapsed,
    setIsCollapsed,
    toggleGroup,
    isGroupExpanded,
    isHydrated,
    isPhone,
  } = usePersistedSidebarState();

  // Le voci abilitate le scrive il backend; finche' la sessione non e' letta
  // (anche al primo render, server e client) non ce n'e' nessuna.
  const navigation = authState?.user?.navigation;
  const enabledKeys = new Set(navigation?.menu_items ?? []);
  const enabledItems = (items: NavItem[]) => items.filter(item => enabledKeys.has(item.key));
  const visibleNavigation = enabledItems(singleNavigation);
  const visibleGroups = groupedNavigation
    .map(group => ({ ...group, items: enabledItems(group.items) }))
    .filter(group => group.items.length > 0);
  const profileHref = authState?.user?.user_id ? `/users/${authState.user.user_id}` : null;

  const isActive = (href: string) => {
    if (!isHydrated) return false; // Prevent hydration mismatch
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  };

  const isGroupActive = (group: NavGroup) => {
    return group.items.some(item => isActive(item.href));
  };

  const handleLogout = async () => {
    try {
      await logout();
      router.push('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Su telefono (<768px) il menu e' NASCOSTO del tutto: resta una barretta con il
  // pulsante-menu, e il menu si apre sopra il contenuto (non lo comprime).
  if (isPhone && isCollapsed) {
    return (
      <div className="flex items-center gap-2 bg-white border-b border-gray-200 px-3 py-2 shadow-sm">
        <button
          type="button"
          onClick={toggleCollapsed}
          className="min-h-[44px] min-w-[44px] rounded-lg hover:bg-gray-100 text-xl"
          aria-expanded={false}
          aria-controls="menu-laterale"
          aria-label="Apri il menu"
        >
          ☰
        </button>
        <span className="text-base font-bold text-gray-900">SportStack</span>
      </div>
    );
  }

  const closeOnNavigate = (event: React.MouseEvent) => {
    if (isPhone && (event.target as HTMLElement).closest('a')) {
      setIsCollapsed(true);
    }
  };

  return (
    <>
    {isPhone && (
      <div
        className="fixed inset-0 z-40 bg-black/50"
        data-testid="menu-backdrop"
        onClick={() => setIsCollapsed(true)}
        aria-hidden="true"
      />
    )}
    <nav
      id="menu-laterale"
      onClick={closeOnNavigate}
      className={isPhone
        ? 'fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] bg-white shadow-lg'
        : `bg-white shadow-lg transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-64'}`}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className={`flex items-center space-x-2 transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}>
            <div className="text-2xl">🏆</div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">SportStack</h1>
              <p className="text-xs text-gray-500">Gestione Competizioni</p>
            </div>
          </div>
          <button
            onClick={toggleCollapsed}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title={isCollapsed ? 'Espandi menu' : 'Comprimi menu'}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? 'Espandi menu laterale' : 'Comprimi menu laterale'}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              {isCollapsed ? '➡️' : '⬅️'}
            </div>
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 p-4 space-y-2 overflow-y-auto">
          {/* Single Navigation Items */}
          {visibleNavigation.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                isActive(item.href)
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={isCollapsed ? `${item.name} - ${item.description}` : ''}
            >
              <div className="text-xl flex-shrink-0">{item.icon}</div>
              <div className={`flex-1 min-w-0 transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
                <div className="font-medium truncate">{item.name}</div>
                <div className="text-xs text-gray-500 truncate">{item.description}</div>
              </div>
              {isActive(item.href) && !isCollapsed && (
                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
              )}
            </Link>
          ))}

          {/* Grouped Navigation Items */}
          {visibleGroups.map((group) => (
            <div key={group.name} className="space-y-1">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.name)}
                className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                  isGroupActive(group)
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
                title={isCollapsed ? `${group.name}` : ''}
                aria-expanded={isGroupExpanded(group.name)}
                aria-label={`${isGroupExpanded(group.name) ? 'Chiudi' : 'Apri'} sezione ${group.name}`}
              >
                <div className="text-xl flex-shrink-0">{group.icon}</div>
                <div className={`flex-1 min-w-0 transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
                  <div className="font-medium truncate text-left">{group.name}</div>
                </div>
                {!isCollapsed && (
                  <div className={`transform transition-transform duration-200 ${
                    isGroupExpanded(group.name) ? 'rotate-90' : 'rotate-0'
                  }`}>
                    ▶️
                  </div>
                )}
              </button>

              {/* Group Items */}
              {isGroupExpanded(group.name) && !isCollapsed && (
                <div className="ml-6 space-y-1 border-l-2 border-gray-200 pl-4">
                  {group.items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 group ${
                        isActive(item.href)
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    >
                      <div className="text-lg flex-shrink-0">{item.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate text-sm">{item.name}</div>
                        <div className="text-xs text-gray-500 truncate">{item.description}</div>
                      </div>
                      {isActive(item.href) && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* User Profile */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 px-3 py-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
              {authState?.user?.first_name?.charAt(0) || 'U'}
            </div>
            <div className={`flex-1 min-w-0 transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
              <div className="text-sm font-medium text-gray-900 truncate">
                {authState?.user?.first_name && authState?.user?.last_name
                  ? `${authState.user.first_name} ${authState.user.last_name}`
                  : authState?.user?.username || 'Utente'
                }
              </div>
              <div className="text-xs text-gray-500 truncate">
                {navigation?.role_label ?? ''}
              </div>
            </div>
          </div>
          <div className={`mt-3 space-y-1 transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
            {profileHref && (
              <Link
                href={profileHref}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors block"
              >
                ⚙️ Il mio profilo
              </Link>
            )}
            <button 
              onClick={handleLogout}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
    </>
  );
}